// Staff-invite routes. BL-1104 tied `index` (POST), `id` (GET), and `pending`
// (GET) to their shared response DTOs per ADR 027 — the client infers the shape
// with no `<T>` and `respond()` type-checks each route against this same
// declaration. `cancel` and `resend` are untouched by that PR and still answer
// with bespoke top-level envelopes; they carry no response marker until the PR
// that touches them migrates them too (touch = migrate).
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
// Type-only imports (erased at build) — keeps the registry client-bundle-safe.
import type {
  InvitePublicDto,
  InviteSendResultDto,
  PendingInviteDto,
} from '../../schemas/responses/invite';

// BL-1833 PR 2b — cancel / resend name their invite in the BODY, so the gate
// loads it from there (`record` + `in: 'body'`) and the tenant is the invite's
// own. An invite with no tenant is the gate's 404, as it was the service's.
const BODY_INVITE = { from: 'record', table: 'user_invite', param: 'inviteId', in: 'body' } as const;

export const invite = {
  cancel: defineAdminRoute({
      path: '/api/invite/cancel',
      params: {},
      methods: { POST: { grant: 'staff.invite', tenant: BODY_INVITE } },
    }),
  id: defineAdminRoute({
      path: '/api/invite/:id',
      params: { id: '' },
      // Untenanted public projection consumed by the accept-invite page.
      // Declared, not bound: the gate would 401 a signed-out invitee.
      methods: {
        GET: {
          grant: { none: 'token — the unguessable invite id is the credential; the signed-out invitee reads the public projection (no token)' },
          tenant: { from: 'none', reason: 'pre-auth — no staff session or membership exists yet' },
          response: responseType<InvitePublicDto>(),
        },
      },
    }),
  index: defineAdminRoute({
      path: '/api/invite',
      params: {},
      methods: {
        POST: {
          grant: 'staff.invite',
          tenant: { from: 'input', field: 'tenantId', in: 'body' },
          response: responseType<InviteSendResultDto>(),
        },
      },
    }),
  pending: defineAdminRoute({
      path: '/api/invite/pending',
      params: {},
      methods: {
        GET: {
          grant: 'staff.read',
          tenant: { from: 'input', field: 'tenantId', in: 'query' },
          response: responseType<PendingInviteDto[]>(),
          query: { tenantId: '' },
        },
      },
    }),
  resend: defineAdminRoute({
      path: '/api/invite/resend',
      params: {},
      methods: { POST: { grant: 'staff.invite', tenant: BODY_INVITE } },
    }),
};
